import { lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';

import PrivateRoutes from './PrivateRoute';
import { ROUTES_PATHS } from '../utils/RoutesPaths';
import Layout from '../components/Layout';
import Error from '../containers/Error';

// Lazy load all route components for code splitting
const Login = lazy(() => import('../containers/Auth/Login'));
const Users = lazy(() => import('../containers/User/UsersList'));
const UserDetails = lazy(() => import('../containers/User/UserDetails'));
const RewardsForm = lazy(() => import('../containers/Rewards/RewardsForm'));
const RewardsDetails = lazy(() =>
	import('../containers/Rewards/RewardsDetails'),
);
const Trips = lazy(() => import('../containers/Trips/TripsList'));
const TripsDetails = lazy(() => import('../containers/Trips/TripsDetails'));
const PendingDrivers = lazy(() => import('../containers/User/PendingDrivers'));
const FaqMobile = lazy(() => import('../containers/FaqMobile'));
const PendingVerificationRequests = lazy(() =>
	import('../containers/VerificationRequests/PendingRequests'),
);
const VerificationRequestsHistory = lazy(() =>
	import('../containers/VerificationRequests/RequestsHistory'),
);
const StandardAdmins = lazy(() =>
	import('../containers/Admins/StandardAdminsList'),
);
const StandardAdminsForm = lazy(() =>
	import('../containers/Admins/StandardAdminsForm'),
);
const ActivateAccount = lazy(() =>
	import('../containers/Auth/ActivateAccount'),
);
const ReferralsList = lazy(() =>
	import('../containers/ReferralsProgram/ReferralsList'),
);
const ReferralsRewards = lazy(() =>
	import('../containers/ReferralsProgram/ReferralsRewards'),
);
const LiveRewards = lazy(() =>
	import('../containers/Rewards/RewardsList/LiveRewards'),
);
const DeletedRewards = lazy(() =>
	import('../containers/Rewards/RewardsList/DeletedRewards'),
);
const OffersList = lazy(() => import('../containers/Offers/OffersList'));
const OfferForm = lazy(() => import('../containers/Offers/OffersForm'));
const OfferDetails = lazy(() => import('../containers/Offers/OfferDetails'));
const AnimationsForm = lazy(() =>
	import('../containers/Animation/AnimationsForm'),
);
const AnimationsList = lazy(() =>
	import('../containers/Animation/AnimationsList'),
);
const AnimationDetails = lazy(() =>
	import('../containers/Animation/AnimationDetails'),
);
const ServiceCenters = lazy(() =>
	import('../containers/Services/ServiceCenters'),
);
const Marketplace = lazy(() => import("../containers/Services/Marketplace"));
const ChallengesList = lazy(() => import("../containers/Challenges/ChallengesList"));
const ChallengesForm = lazy(() => import("../containers/Challenges/ChallengesForm"));
const ChallengeDetails = lazy(() => import("../containers/Challenges/ChallengeDetails"));

// Loading fallback component for lazy-loaded routes
// Using inline styles and MUI components for immediate rendering without theme dependencies
const LoadingFallback = () => (
	<Box
		sx={{
			position: 'fixed',
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			backgroundColor: '#172c49',
			zIndex: 9999,
		}}
	>
		<CircularProgress />
	</Box>
);

const AppRoutes = () => {
  const { admin } = useSelector((state) => state.auth);
  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route
            path={ROUTES_PATHS.login}
            element={
              !!admin?.auth_token ? (
                <Navigate to={ROUTES_PATHS.fleetOwners} />
              ) : (
                <Login />
              )
            }
          />
          <Route
            path={ROUTES_PATHS.activateAccount}
            element={
              !!admin?.auth_token ? (
                <Navigate to={ROUTES_PATHS.fleetOwners} />
              ) : (
                <ActivateAccount />
              )
            }
          />
          <Route
            path={ROUTES_PATHS.resetPassword}
            element={
              !!admin?.auth_token ? (
                <Navigate to={ROUTES_PATHS.fleetOwners} />
              ) : (
                <ActivateAccount />
              )
            }
          />
          <Route
            element={
              !!admin?.auth_token ? (
                <Layout />
              ) : (
                <Navigate to={ROUTES_PATHS.login} />
              )
            }
          >
            <Route path={ROUTES_PATHS.singleOwners}>
              <Route index element={<Users />} />
              <Route
                path={ROUTES_PATHS.userDetailsParam}
                element={<UserDetails />}
              />
            </Route>
            <Route path={ROUTES_PATHS.fleetOwners}>
              <Route index element={<Users />} />
              <Route
                path={ROUTES_PATHS.userDetailsParam}
                element={<UserDetails />}
              />
            </Route>
            <Route path={ROUTES_PATHS.drivers}>
              <Route index element={<Users />} />
              <Route
                path={ROUTES_PATHS.userDetailsParam}
                element={<UserDetails />}
              />
            </Route>
            <Route path={ROUTES_PATHS.pendingDrivers}>
              <Route index element={<PendingDrivers />} />
            </Route>
            <Route path={ROUTES_PATHS.verificationRequestsPending}>
              <Route index element={<PendingVerificationRequests />} />
            </Route>
            <Route path={ROUTES_PATHS.verificationRequestsHistory}>
              <Route index element={<VerificationRequestsHistory />} />
            </Route>
            <Route path={ROUTES_PATHS.liveRewards} element={<PrivateRoutes />}>
              <Route index element={<LiveRewards />} />
              <Route
                path={ROUTES_PATHS.rewardsDetailsParam}
                element={<RewardsDetails />}
              />
              <Route path={ROUTES_PATHS.addRewards} element={<RewardsForm />} />
            </Route>
            <Route
              path={ROUTES_PATHS.deletedRewards}
              element={<PrivateRoutes />}
            >
              <Route index element={<DeletedRewards />} />
              <Route
                path={ROUTES_PATHS.deletedRewardsDetails}
                element={<RewardsDetails />}
              />
            </Route>
            <Route path={ROUTES_PATHS.tripsOwners}>
              <Route index element={<Trips />} />
              <Route
                path={ROUTES_PATHS.tripsDetailsParam}
                element={<TripsDetails />}
              />
            </Route>
            <Route path={ROUTES_PATHS.tripsDrivers}>
              <Route index element={<Trips />} />
              <Route
                path={ROUTES_PATHS.tripsDetailsParam}
                element={<TripsDetails />}
              />
            </Route>
            <Route
              path={ROUTES_PATHS.standardAdmins}
              element={<PrivateRoutes />}
            >
              <Route index element={<StandardAdmins />} />
              <Route
                path={ROUTES_PATHS.addStandardAdmins}
                element={<StandardAdminsForm />}
              />
            </Route>
            <Route path={ROUTES_PATHS.referralsList}>
              <Route index element={<ReferralsList />} />
            </Route>
            <Route path={ROUTES_PATHS.referralsRewards}>
              <Route index element={<ReferralsRewards />} />
            </Route>
            <Route path={ROUTES_PATHS.offers}>
              <Route index element={<OffersList />} />
              <Route
                path={ROUTES_PATHS.offersDetailsParam}
                element={<OfferDetails />}
              />
              <Route path={ROUTES_PATHS.offersEdit} element={<OfferForm />} />
              <Route path={ROUTES_PATHS.offersAdd} element={<OfferForm />} />
            </Route>
            <Route path={ROUTES_PATHS.animations}>
              <Route index element={<AnimationsList />} />
              <Route
                path={ROUTES_PATHS.animationDetailsParam}
                element={<AnimationDetails />}
              />
              <Route
                path={ROUTES_PATHS.addAnimation}
                element={<AnimationsForm />}
              />
              <Route
                path={ROUTES_PATHS.editAnimation}
                element={<AnimationsForm />}
              />
            </Route>
            <Route path={ROUTES_PATHS.services}>
              <Route index element={<ServiceCenters />} />
              <Route
                path={ROUTES_PATHS.serviceCenters}
                element={<ServiceCenters />}
              />
              <Route
                path={ROUTES_PATHS.marketplace}
                element={<Marketplace />}
              />
            </Route>
            <Route path={ROUTES_PATHS.challenges}>
              <Route index element={<ChallengesList />} />
              <Route
                path={ROUTES_PATHS.challengesDetailsParam}
                element={<ChallengeDetails />}
              />
              <Route element={<PrivateRoutes />}>
                <Route
                  path={ROUTES_PATHS.challengesAddParam}
                  element={<ChallengesForm />}
                />
                <Route
                  path={ROUTES_PATHS.challengesEditParam}
                  element={<ChallengesForm />}
                />
                <Route
                  path={ROUTES_PATHS.challengesDuplicateParam}
                  element={<ChallengesForm />}
                />
              </Route>
            </Route>
            <Route path="*" element={<Error />} />
          </Route>
          <Route path={ROUTES_PATHS.faqMobile} element={<FaqMobile />} />
          <Route
            path={ROUTES_PATHS.root}
            element={<Navigate to={ROUTES_PATHS.fleetOwners} />}
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default AppRoutes;
